import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { NUDGER_PROVISION_LABELS, NudgerRule, NudgerRuleField, NudgerRuleParameters, NudgerRuleUpdateRequest } from '../../_models/admin/nudger-rule.models';
import { NudgerRulesService } from '../../_services/nudger-rules/nudger-rules.service';
import { DynamicConfirmModalComponent } from '../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { NudgerRuleHistoryData, NudgerRuleHistoryDrawerComponent } from './nudger-rule-history-drawer.component';
import { AdminEmailComponent } from '../../shared/admin-ux/admin-email.component';

/** The unsaved state of one rule's form. Field values are held as the text in the inputs. */
interface NudgerRuleDraft {
  isEnabled: boolean;
  values: { [key: string]: string };
}

const TIME_PATTERN = /^([01]?\d|2[0-3]):[0-5]\d$/;

/**
 * Tutor nudger rule editor: one form per rule type with labelled, validated fields (never raw JSON), an on/off
 * switch, who changed it and when, and the change history. The fields each rule has come from the API, which
 * checks every value again. Lives on the Nudger tab of System Controls.
 */
@Component({
  selector: 'nudger-rules-editor',
  standalone: true,
  imports: [DatePipe, AdminEmailComponent],
  templateUrl: 'nudger-rules-editor.component.html'
})
export class NudgerRulesEditorComponent implements OnInit {

  public rules: NudgerRule[] = [];
  public drafts: { [workFlowType: number]: NudgerRuleDraft } = {};
  public loading = false;
  public saving: number = null;
  public error: string = null;
  public readonly provisionLabels = NUDGER_PROVISION_LABELS;
  public readonly provisionValues = ['Y', 'N'];

  constructor(private service: NudgerRulesService, private dialog: MatDialog) { }

  ngOnInit(): void {
    this.load();
  }

  public load(): void {
    this.loading = true;
    this.error = null;
    this.service.getRules().subscribe({
      next: rules => {
        this.loading = false;
        this.rules = rules || [];
        this.drafts = {};
        this.rules.forEach(rule => this.drafts[rule.workFlowType] = this.draftOf(rule));
      },
      error: error => {
        this.loading = false;
        this.error = (error && error.message) || 'Could not load the nudger rules.';
      }
    });
  }

  public value(rule: NudgerRule, field: NudgerRuleField): string {
    const draft = this.drafts[rule.workFlowType];
    return draft ? (draft.values[field.key] ?? '') : '';
  }

  public edit(rule: NudgerRule, field: NudgerRuleField, value: string): void {
    const draft = this.drafts[rule.workFlowType];
    this.drafts = { ...this.drafts, [rule.workFlowType]: { ...draft, values: { ...draft.values, [field.key]: value ?? '' } } };
  }

  public setEnabled(rule: NudgerRule, enabled: boolean): void {
    const draft = this.drafts[rule.workFlowType];
    this.drafts = { ...this.drafts, [rule.workFlowType]: { ...draft, isEnabled: enabled } };
  }

  public isEnabled(rule: NudgerRule): boolean {
    const draft = this.drafts[rule.workFlowType];
    return draft ? draft.isEnabled : rule.isEnabled;
  }

  public isDirty(rule: NudgerRule): boolean {
    const draft = this.drafts[rule.workFlowType];
    if (!draft) {
      return false;
    }
    const original = this.draftOf(rule);
    return draft.isEnabled !== original.isEnabled
      || rule.fields.some(f => (draft.values[f.key] ?? '').trim() !== (original.values[f.key] ?? '').trim());
  }

  /** Why the field's unsaved value cannot be saved, or null when it can. Mirrors the API's 422 rules. */
  public validate(rule: NudgerRule, field: NudgerRuleField): string | null {
    const text = this.value(rule, field).trim();
    switch (field.kind) {
      case 'int': {
        if (!/^-?\d+$/.test(text)) {
          return 'Enter a whole number.';
        }
        const n = Number(text);
        if (field.min != null && n < field.min) {
          return 'The lowest allowed value is ' + field.min + '.';
        }
        if (field.max != null && n > field.max) {
          return 'The highest allowed value is ' + field.max + '.';
        }
        return null;
      }
      case 'time':
        return TIME_PATTERN.test(text) ? null : 'Enter a time between 00:00 and 23:59.';
      case 'provision':
        return text === 'Y' || text === 'N' ? null : 'Choose which sessions the rule looks at.';
      default:
        return null;
    }
  }

  public isValid(rule: NudgerRule): boolean {
    return rule.fields.every(f => !this.validate(rule, f));
  }

  public canSave(rule: NudgerRule): boolean {
    return this.isDirty(rule) && this.isValid(rule) && this.saving !== rule.workFlowType;
  }

  /** Ask first — the rule applies to every tutor from the poller's next run — then save the rule's full state. */
  public save(rule: NudgerRule): void {
    if (!this.canSave(rule)) {
      return;
    }
    const request = this.requestOf(rule);
    const message = !request.isEnabled && rule.isEnabled
      ? 'Turn off "' + rule.name + '"? It will raise no new nudges until it is turned back on.'
      : 'Save "' + rule.name + '"? The change applies to every tutor from the next nudger run.';

    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: message, confirmLabel: 'Save' }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.put(rule, request);
        }
      });
  }

  /** Puts the form back to the rule as last saved. */
  public reset(rule: NudgerRule): void {
    this.drafts = { ...this.drafts, [rule.workFlowType]: this.draftOf(rule) };
  }

  public history(rule: NudgerRule): void {
    const data: NudgerRuleHistoryData = { rule };
    this.dialog.open(NudgerRuleHistoryDrawerComponent, {
      data,
      width: '520px',
      height: '100vh',
      maxWidth: '100vw',
      position: { right: '0', top: '0' }
    });
  }

  public inputId(rule: NudgerRule, field: NudgerRuleField): string {
    return 'nudger-' + rule.workFlowType + '-' + field.key;
  }

  private put(rule: NudgerRule, request: NudgerRuleUpdateRequest): void {
    this.saving = rule.workFlowType;
    this.error = null;
    this.service.saveRule(rule.workFlowType, request).subscribe({
      next: updated => {
        this.saving = null;
        const saved = updated || rule;
        this.rules = this.rules.map(r => r.workFlowType === rule.workFlowType ? saved : r);
        this.drafts = { ...this.drafts, [rule.workFlowType]: this.draftOf(saved) };
      },
      error: error => {
        this.saving = null;
        this.error = 'Could not save "' + rule.name + '": ' + ((error && error.message) || 'please try again.');
      }
    });
  }

  private draftOf(rule: NudgerRule): NudgerRuleDraft {
    const values: { [key: string]: string } = {};
    (rule.fields || []).forEach(f => {
      const v = rule.parameters ? rule.parameters[f.key] : null;
      values[f.key] = v === null || v === undefined ? '' : String(v);
    });
    return { isEnabled: rule.isEnabled, values };
  }

  private requestOf(rule: NudgerRule): NudgerRuleUpdateRequest {
    const draft = this.drafts[rule.workFlowType];
    const parameters: NudgerRuleParameters = {
      alternativeProvision: null,
      minutesSinceSessionStarted: null,
      minutesSinceSessionEnded: null,
      runTime: null,
      endTime: null,
      weeksSinceOutCome: null,
      weeksSincePlacementEnd: null,
      outcomeWithinDaysPlacementEnd: null
    };
    rule.fields.forEach(f => {
      const text = (draft.values[f.key] ?? '').trim();
      (parameters as any)[f.key] = f.kind === 'int' ? Number(text) : text;
    });
    return { isEnabled: draft.isEnabled, parameters };
  }
}
